import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, EyeIcon } from "@heroicons/react/24/outline";
import { Container, EmptyState, Loading, PageHeader, RowAction, Table, Tag } from "../../ui";
import { KINDS, destinationOf, formatKigaliDate, kindOf, useStaffScholarship } from "../model";
import LoansPanel from "./LoansPanel";
import SignOffPanel from "./SignOffPanel";
import AwardListPanel from "../review/AwardListPanel";
import { myLoanRole, useNominations } from "./model";

const progressTag = (s) => (s.state === "DECIDED" ? <Tag color="green">Decided</Tag> : <Tag color="yellow">In progress</Tag>);

// Sponsored scholarships whose nomination list HEC sent to the DG for sign-off, then to the sponsor (Scholarships › Nominations)
export function NominationsPage() {
  const navigate = useNavigate();
  const { data = [], isPending, isError, refetch } = useNominations();
  const open = (s) => navigate(`${KINDS.scholarship.approvalsPath}/${s.id}`);

  const columns = [
    { key: "name", label: "Scholarship", render: (s) => <span className="font-medium text-slate-800">{s.name}</span>, accessor: (s) => s.name },
    { key: "sponsor", label: "Partner", accessor: (s) => s.sponsor?.name ?? "—" },
    { key: "country", label: "Country", accessor: (s) => destinationOf(s) },
    { key: "academicYear", label: "Academic year", accessor: (s) => s.academicYear?.name ?? "—" },
    { key: "sentToDgAt", label: "Sent to the DG", accessor: (s) => formatKigaliDate(s.sentToDgAt), sortValue: (s) => s.sentToDgAt },
    { key: "state", label: "State", accessor: (s) => (s.state === "DECIDED" ? "Decided" : "In progress"), render: progressTag },
    { key: "open", label: "", exportable: false, render: (s) => <RowAction Icon={EyeIcon} label="Nominations" onClick={() => open(s)} /> },
  ];

  return (
    <Container className="flex flex-col gap-6 py-6">
      <PageHeader title="Nominations" subtitle="Nomination lists HEC sent to the DG for sign-off, then to the partner." />
      {isError ? (
        <EmptyState title="Could not load the nominations" onRefresh={refetch} />
      ) : (
        <Table cardView columns={columns} data={data} onRowClick={open} isLoading={isPending} searchable={false} exportName="nominations" emptyTitle="No nominations sent to the DG yet" />
      )}
    </Container>
  );
}

// One scholarship's awards: a government loan's are decided one by one by the DG, the Minister or
// HEC; any other scholarship's list is signed off (or returned) as a whole by the DG
export function LoanApprovalPage() {
  const { scholarshipId } = useParams();
  const { data, isPending, isError, refetch } = useStaffScholarship(scholarshipId);
  const scholarship = data?.data;

  if (isPending) return <Loading label="Loading the scholarship…" />;
  if (isError || !scholarship?.sentToDgAt) return <EmptyState title="Could not load this scholarship" onRefresh={refetch} />;

  return (
    <Container className="flex flex-col gap-6 py-6">
      <Link to={kindOf(scholarship.type).approvalsPath} className="flex w-fit items-center gap-1 text-sm font-medium text-brand hover:underline">
        <ArrowLeftIcon className="h-4 w-4" /> {kindOf(scholarship.type).isLoan ? "All loan approvals" : "All nominations"}
      </Link>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            {scholarship.name}
            {progressTag(scholarship)}
          </span>
        }
        subtitle={`${destinationOf(scholarship)} · ${scholarship.academicYear?.name ?? ""}`}
      />
      {kindOf(scholarship.type).isLoan ? (
        <LoansPanel scholarship={scholarship} role={myLoanRole()} />
      ) : (
        <>
          <SignOffPanel scholarship={scholarship} />
          <AwardListPanel scholarship={scholarship} />
        </>
      )}
    </Container>
  );
}
